import { TranscriptEntry } from './index';

export type { TranscriptEntry };

export interface AICoachingMessage {
	id: string;
	text: string;
	timestamp: Date;
	isStreaming: boolean;
	role: 'user' | 'assistant'; // Distinguish transcript/questions from AI responses
	messageType?: 'transcript' | 'question' | 'system'; // For user messages: transcript sent, direct question, or system notification
}

export interface TysonCoachingContextData {
	phoneCallId?: string;
	companyId: string;
	leadServedId?: string; // Optional for practice calls (tyson-practice mode)
	contactId: string;
	objectivePrompt?: string; // Coaching system prompt from backend
	agentTemplateId?: string; // Optional: specific coaching agent template to use
}

export interface TranscriptBatch {
	entries: TranscriptEntry[];
	timestamp: Date;
}

export interface LiveUtterance {
	sequenceId: string;
	speaker: 'Lead' | 'Sales Rep';
	transcription: string;
	final: boolean;
	timestamp: Date;
	// New fields for partial utterance handling
	stableText?: string;
	speculativeText?: string;
}

/**
 * Partial utterance tracking for real-time transcription.
 * Handles out-of-order events and stability pairs from Twilio.
 */
export interface PartialUtterance {
	sequenceId: number; // Parsed from string for ordering
	speaker: 'Lead' | 'Sales Rep';
	stableText: string; // stability >= 0.5
	speculativeText: string; // stability < 0.5
	final: boolean;
	timestamp: Date;
	highestSeqSeen: number; // For out-of-order rejection
}

export const TYSON_ASSISTANT_NAME = 'SalesLiveCoachingAssistant';

/**
 * Kickoff request that asks the coach for the opening Game Plan on a freshly
 * created conversation. Sent as a User message on the first stream alongside
 * `autoStart: true` — `autoStart` makes the server-side agent inject its
 * persona/objective starter prompt, while this message is the explicit ask
 * that actually produces a Game Plan reply (the first assistant message the
 * coaching panel pins as the Game Plan card).
 *
 * This was dropped when System-role sends migrated to AutoStart (#9288); the
 * agent's AutoStart starter only greets and does not generate a plan on its
 * own, so the explicit request is restored here. Mirrors the focused view's
 * `before_call` "Game Plan" keyword pin, which rides `autoStart` the same way.
 */
export const TYSON_GAME_PLAN_KICKOFF_PROMPT = `I am about to start calling contacts for this lead. Search their website and then based on everything you know, give me a quick Game Plan for these calls.

I will let you know each time I start a new call with a specific contact so you can give me live coaching.`;

export const TYSON_END_OF_CALL_SUMMARY_PROMPT = `The call has ended. Based on the entire conversation transcript and our training materials, please provide a comprehensive summary:

## What Went Well
- Highlight 2-3 specific things the SDR did effectively

## Areas for Improvement
- Identify 2-3 specific areas where the SDR could improve
- Reference relevant techniques from our training materials

## Focus for Next Call
- Provide 1-2 actionable items to practice on the next call

Be specific and reference actual moments from the call.`;
